import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { parkingApi, usePkList, usePkMutation, type PkTicket, type TicketStatus } from "../api/parkingApi";
import { ConsolePage, DetailRow, Drawer, FilterSelect, LoadState, SearchBox, SegmentedToggle, StatusChip, ToolButton, formatDateTime, relativeTime, toneOf, useTicker } from "../components/console/Console";
import FormModal, { type FormValues } from "../components/console/FormModal";
import { LotSelect, PARKING_BASE, PlateBadge, TICKET_TONE, duration, errorText, minutesBetween, money } from "./parkingUi";
import { buildTicketPdf, openPdf } from "./parkingPdf";

// ===========================================================================
// Control de estacionamientos → Tickets: lista con la foto de entrada (LPR),
// filtros por estacionamiento, estado y periodo, y detalle con el desglose del
// importe, reimpresión del ticket o recibo, boleto perdido, cancelación y
// salida.
// ===========================================================================

const STATUSES: TicketStatus[] = ["open", "paid", "exited", "lost", "cancelled"];
const PAGE = 50;

export function FeeBreakdown({ ticket }: { ticket: PkTicket }) {
  const { t, i18n } = useTranslation("parking");
  const a = ticket.amount;
  if (!a) return <p className="text-sm text-content-secondary">{t("tickets.notCharged")}</p>;
  return (
    <ul className="space-y-1 text-sm">
      {a.lines.map((l, i) => (
        <li key={i} className="flex justify-between">
          <span className="text-content-secondary">{t(`fee.${l.concept}`)}</span>
          <span className="tabular-nums text-content">{money(l.amount, i18n.language)}</span>
        </li>
      ))}
      {a.overstay_total ? (
        <li className="flex justify-between"><span className="text-content-secondary">{t("fee.overstay")}</span><span className="tabular-nums">{money(a.overstay_total, i18n.language)}</span></li>
      ) : null}
      <li className="flex justify-between border-t border-line pt-1 font-semibold"><span>{t("common.total")}</span><span className="tabular-nums">{money(a.total, i18n.language)}</span></li>
      <li className="text-[11px] text-content-muted">{t("tickets.billedMinutes", { interpolation: { escapeValue: false }, minutes: a.minutes })}{a.capped ? ` · ${t("fee.capped")}` : ""}{a.night ? ` · ${t("fee.night_rate")}` : ""}</li>
    </ul>
  );
}

export default function TicketsScreen() {
  const { t, i18n } = useTranslation("parking");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const now = useTicker(30_000);
  const [lot, setLot] = useState("");
  const [status, setStatus] = useState("");
  const [days, setDays] = useState<number>(7);
  const [search, setSearch] = useState(params.get("folio") || "");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<PkTicket | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const since = useMemo(() => new Date(Date.now() - days * 86400_000).toISOString(), [days]);
  const lotsQ = usePkList("lots");
  const q = usePkList("tickets", { lot_id: lot || undefined, status: status || undefined, since, search: search || undefined, limit: 1000 }, { refetchInterval: 30_000 });
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));

  // Enlace directo a un ticket (desde el mapa o el tablero).
  useEffect(() => {
    const id = params.get("ticket");
    if (!id) return;
    parkingApi
      .get("tickets", id)
      .then(setSelected)
      .catch(() => undefined);
  }, [params]);
  useEffect(() => setPage(1), [lot, status, days, search]);

  const lost = usePkMutation((id: string) => parkingApi.lost(id));
  const cancel = usePkMutation(({ id, reason }: { id: string; reason: string }) => parkingApi.cancel(id, reason));
  const exit = usePkMutation((id: string) => parkingApi.exit({ ticket_id: id }));

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      setSelected(null);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  const reprint = async (tk: PkTicket) => {
    const payment = tk.payment_id ? await parkingApi.get("payments", tk.payment_id).catch(() => null) : null;
    openPdf(await buildTicketPdf({ ticket: tk, payment, t, lang: i18n.language }), `${tk.folio}.pdf`);
  };

  return (
    <ConsolePage
      icon="ri-ticket-2-line"
      title={t("tickets.title")}
      subtitle={t("tickets.subtitle", { interpolation: { escapeValue: false }, count: rows.length })}
      actions={<ToolButton icon="ri-cash-line" variant="primary" onClick={() => navigate(`${PARKING_BASE}/cobro`)}>{t("dashboard.charge")}</ToolButton>}
    >
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={search} onChange={setSearch} placeholder={t("tickets.searchPlaceholder")} />
        <LotSelect lots={lotsQ.data ?? []} value={lot} onChange={setLot} />
        <FilterSelect value={status} onChange={setStatus} label={t("common.status")} options={[{ value: "", label: t("common.allStatuses") }, ...STATUSES.map((s) => ({ value: s, label: t(`ticketStatus.${s}`) }))]} />
        <SegmentedToggle value={days} onChange={setDays} label={t("tickets.period")} options={[1, 7, 14, 30].map((d) => ({ value: d, label: t("tickets.lastDays", { interpolation: { escapeValue: false }, count: d }) }))} />
      </div>
      <LoadState loading={q.isLoading} error={q.error} empty={!rows.length} emptyText={t("tickets.empty")} onRetry={() => q.refetch()}>
        <div className="overflow-x-auto rounded-xl border border-line bg-card">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-2">{t("tickets.photo")}</th>
                <th className="px-3 py-2">{t("common.folio")}</th>
                <th className="px-3 py-2">{t("common.plate")}</th>
                <th className="px-3 py-2">{t("common.lot")}</th>
                <th className="px-3 py-2">{t("tickets.entry")}</th>
                <th className="px-3 py-2">{t("tickets.stay")}</th>
                <th className="px-3 py-2 text-right">{t("common.total")}</th>
                <th className="px-3 py-2">{t("common.status")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.slice((page - 1) * PAGE, page * PAGE).map((tk) => (
                <tr key={tk.id} className="cursor-pointer hover:bg-subtle" onClick={() => setSelected(tk)}>
                  <td className="px-3 py-1.5">
                    {tk.entry_photo_url ? <img src={tk.entry_photo_url} alt="" loading="lazy" className="h-9 w-14 rounded object-cover" /> : <span className="flex h-9 w-14 items-center justify-center rounded bg-subtle"><i className="ri-car-line text-content-muted" /></span>}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs text-content">{tk.folio}</td>
                  <td className="px-3 py-1.5"><PlateBadge plate={tk.plate} /></td>
                  <td className="max-w-[200px] truncate px-3 py-1.5 text-content-secondary">{tk.lot_name}</td>
                  <td className="px-3 py-1.5 text-content-secondary" title={formatDateTime(tk.entry_at, i18n.language)}>{relativeTime(tk.entry_at, i18n.language, now)}</td>
                  <td className="px-3 py-1.5 tabular-nums text-content-secondary">{duration(minutesBetween(tk.entry_at, tk.exit_at, now))}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-content">{tk.amount ? money(tk.amount.total, i18n.language) : "—"}</td>
                  <td className="px-3 py-1.5">
                    <StatusChip tone={toneOf(TICKET_TONE, tk.status)}>{t(`ticketStatus.${tk.status}`)}</StatusChip>
                    {tk.invoice_status && <i className="ri-file-text-line ml-1 text-content-muted" title={t(`invoiceStatus.${tk.invoice_status}`)} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-end gap-2 text-sm text-content-secondary">
          <ToolButton icon="ri-arrow-left-s-line" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} />
          <span>{t("common.pageOf", { interpolation: { escapeValue: false }, page, total: pages })}</span>
          <ToolButton icon="ri-arrow-right-s-line" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} />
        </div>
      </LoadState>

      <Drawer
        open={Boolean(selected)}
        title={selected ? `${t("common.ticket")} ${selected.folio}` : ""}
        subtitle={selected?.lot_name}
        onClose={() => setSelected(null)}
        footer={
          selected && (
            <>
              <ToolButton icon="ri-printer-line" onClick={() => reprint(selected)}>{selected.payment_id ? t("tickets.printReceipt") : t("tickets.printTicket")}</ToolButton>
              {["open", "lost"].includes(selected.status) && <ToolButton icon="ri-cash-line" variant="primary" onClick={() => navigate(`${PARKING_BASE}/cobro?ticket=${encodeURIComponent(selected.id)}`)}>{t("occupancy.charge")}</ToolButton>}
              {selected.status === "open" && <ToolButton icon="ri-question-line" onClick={() => act(() => lost.mutateAsync(selected.id), t("tickets.markedLost"))}>{t("tickets.markLost")}</ToolButton>}
              {selected.status === "paid" && <ToolButton icon="ri-logout-box-r-line" onClick={() => act(() => exit.mutateAsync(selected.id), t("tickets.exited"))}>{t("tickets.registerExit")}</ToolButton>}
              {["open", "lost"].includes(selected.status) && <ToolButton icon="ri-close-circle-line" variant="danger" onClick={() => setCancelOpen(true)}>{t("tickets.cancel")}</ToolButton>}
              {["paid", "exited"].includes(selected.status) && !selected.invoice_status && (selected.amount?.total ?? 0) > 0 && (
                <ToolButton icon="ri-file-text-line" onClick={() => navigate(`${PARKING_BASE}/facturacion?folio=${encodeURIComponent(selected.folio)}`)}>{t("tickets.invoice")}</ToolButton>
              )}
            </>
          )
        }
      >
        {selected && (
          <div className="space-y-4">
            {selected.entry_photo_url && (
              <figure className="overflow-hidden rounded-xl border border-line">
                <img src={selected.entry_photo_url} alt={t("tickets.entryPhoto")} className="aspect-video w-full object-cover" />
                <figcaption className="flex items-center justify-between bg-subtle px-3 py-1.5 text-[11px] text-content-secondary">
                  <span><i className="ri-camera-lens-line" /> {selected.entry_method === "lpr" ? t("tickets.lpr") : t("tickets.manual")}</span>
                  <PlateBadge plate={selected.plate} />
                </figcaption>
              </figure>
            )}
            <div>
              <DetailRow label={t("common.status")}><StatusChip tone={toneOf(TICKET_TONE, selected.status)}>{t(`ticketStatus.${selected.status}`)}</StatusChip></DetailRow>
              <DetailRow label={t("tickets.entry")}>{formatDateTime(selected.entry_at, i18n.language)}</DetailRow>
              {selected.paid_at && <DetailRow label={t("tickets.paidAt")}>{formatDateTime(selected.paid_at, i18n.language)}</DetailRow>}
              {selected.exit_at && <DetailRow label={t("tickets.exit")}>{formatDateTime(selected.exit_at, i18n.language)}</DetailRow>}
              <DetailRow label={t("tickets.stay")}>{duration(minutesBetween(selected.entry_at, selected.exit_at, now))}</DetailRow>
              <DetailRow label={t("tickets.space")}>{selected.space_code ?? "—"}</DetailRow>
              <DetailRow label={t("tickets.rate")}>{selected.rate_name ?? "—"}</DetailRow>
              {selected.payment_method && <DetailRow label={t("tickets.method")}>{t(`method.${selected.payment_method}`)}</DetailRow>}
              {selected.validation_code && <DetailRow label={t("tickets.validation")}>{selected.validation_code}</DetailRow>}
              {selected.invoice_status && <DetailRow label={t("tickets.invoiceStatus")}>{t(`invoiceStatus.${selected.invoice_status}`)}</DetailRow>}
              {selected.cancel_reason && <DetailRow label={t("tickets.cancelReason")}>{selected.cancel_reason}</DetailRow>}
            </div>
            <section className="rounded-xl border border-line p-3">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-muted">{t("tickets.breakdown")}</h3>
              <FeeBreakdown ticket={selected} />
            </section>
          </div>
        )}
      </Drawer>

      {cancelOpen && selected && (
        <FormModal
          title={t("tickets.cancelTitle", { interpolation: { escapeValue: false }, folio: selected.folio })}
          submitLabel={t("tickets.cancel")}
          busy={cancel.isPending}
          onClose={() => setCancelOpen(false)}
          onSubmit={async (v: FormValues) => {
            await act(() => cancel.mutateAsync({ id: selected.id, reason: String(v.reason || "") }), t("tickets.cancelled"));
            setCancelOpen(false);
          }}
          fields={[{ key: "reason", label: t("tickets.cancelReason"), type: "textarea", required: true, placeholder: t("tickets.cancelPlaceholder") }]}
        />
      )}
    </ConsolePage>
  );
}
